import {useRef, useState} from 'react'
import {useNavigate} from 'react-router'
import isEqual from 'lodash/isEqual'
import some from 'lodash/some'
import type {Moment} from 'moment-timezone'
import moment from 'moment-timezone'
import {fromJust, catMaybes, mmap, fromMaybe} from '@freckle/maybe'
import {mkNonEmptySingleton} from '@freckle/non-empty'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {
  defaultStartDate,
  defaultEndDate,
} from '@freckle/educator-entities/ts/common/helpers/date-helper'
import {staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import type {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import {useCourses} from '@freckle/educator-entities/ts/common/hooks/use-courses'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import {getUnarchivedModels} from '@freckle/educator-entities/ts/roster/collections/courses'
import type {CourseAttrs} from '@freckle/educator-entities/ts/roster/models/course'
import type {CourseMembershipAttrs} from '@freckle/educator-entities/ts/roster/models/course-membership'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {WithResources} from '@freckle/school/ts/common/components/with-resources'
import type {
  AllProvidersOrSpecificT,
  DashboardIdentityProviderT,
} from '@freckle/school/ts/common/helpers/api-helper'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {useStudents} from '@freckle/school/ts/hooks/use-students'
import {Growth} from '@freckle/school/ts/pages/growth/components/growth'
import {DEFAULT_ELA_MIN_DATE} from '@freckle/school/ts/pages/growth/components/growth/constants'
import {indexStudentsById} from '@freckle/school/ts/users/collections/students'
import type {StudentsMapT} from '@freckle/school/ts/users/collections/students'
import {forTeacher} from '@freckle/school/ts/users/collections/students'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'

type Props = {
  subject: SubjectT | undefined | null
  schools: Array<SchoolAttrs>
  schoolId: number | undefined | null
  teachers: Array<BaseTeacherAttrs>
  courseMemberships: Array<CourseMembershipAttrs>
  teacherId: number | undefined
  studentId: number | undefined | null
  start: number | undefined | null
  end: number | undefined | null
  grade: number | undefined | null
  domain: string | undefined | null
  mathDomains: Array<RlDomainT>
  elaDomains: Array<RlDomainT>
  mathStandardSet: RlStandardSetT
  schoolAdmin: SchoolAdminAttrs
}

export function GrowthContainer(props: Props) {
  const courses = useCourses()
  const students = useStudents()
  return (
    <WithResources
      resources={{courses, students}}
      render={({courses, students}) => (
        <GrowthWrapper {...props} courses={courses} students={students} />
      )}
    />
  )
}

type GrowthWrapperProps = Props & {courses: CourseAttrs[]; students: StudentAttrs[]}

export function GrowthWrapper(props: GrowthWrapperProps) {
  const {
    subject: propsSubject,
    schoolId: propsSchoolId,
    teacherId: propsTeacherId,
    studentId: propsStudentId,
    start,
    end,
    grade: propsGrade,
    domain: propsDomain,
    courseMemberships,
    mathStandardSet,
    mathDomains,
    elaDomains,
    teachers,
    schools,
    courses,
    students,
    schoolAdmin,
  } = props
  const navigate = useNavigate()
  const oldSchoolId = useRef(propsSchoolId)
  const initialSubject = fromMaybe(() => 'math', propsSubject) as SubjectT
  const startDateValue = setStartDateValue(initialSubject, mmap(moment, start))
  const endDateValue = end !== null && end !== undefined ? moment(end) : defaultEndDate
  const idpOriginsData = {tag: 'all' as const}
  const [subject, setSubject] = useState<SubjectT>(initialSubject)
  const [schoolId, setSchoolId] = useState<number | undefined | null>(propsSchoolId)
  const [teacherId, setTeacherId] = useState<number | undefined>(propsTeacherId)
  const [studentId, setStudentId] = useState<number | undefined | null>(propsStudentId)
  const [idpOrigins, setIdpOrigins] = useState<AllProvidersOrSpecificT>(idpOriginsData)
  const [startDate, setStartDate] = useState<Moment>(startDateValue)
  const [endDate, setEndDate] = useState<Moment>(endDateValue)
  const [grade, setGrade] = useState<number | undefined | null>(propsGrade)
  const [domain, setDomain] = useState<string | undefined | null>(propsDomain)
  const [filteredStudents, setFilteredStudents] = useState<Array<StudentAttrs> | null>(null)
  const [unarchivedCourses, setUnarchivedCourses] = useState<Array<CourseAttrs> | null>(null)
  const [indexedByIdStudents, setIndexedByIdStudents] = useState<StudentsMapT | null>(null)

  useSafeEffectExtraDeps<{courses: CourseAttrs[]; students: StudentAttrs[]}>(
    ({courses, students}) => {
      if (teacherId !== undefined) {
        setUnarchivedCourses(getUnarchivedModels(courses))
        setIndexedByIdStudents(indexStudentsById(students))
      }
    },
    [teacherId],
    {
      courses: staticExtra(courses),
      students: staticExtra(students),
    },
  )

  useSafeEffectExtraDeps<{
    courseMemberships: Array<CourseMembershipAttrs>
    indexedByIdStudents: StudentsMapT | null
    unarchivedCourses: Array<CourseAttrs> | null
  }>(
    ({courseMemberships, indexedByIdStudents, unarchivedCourses}) => {
      if (teacherId !== undefined && indexedByIdStudents !== null && unarchivedCourses !== null) {
        const filtered = getFilteredStudents(
          teacherId,
          indexedByIdStudents,
          unarchivedCourses,
          courseMemberships,
          studentId,
        )
        setFilteredStudents(filtered)
      }
    },
    [teacherId, studentId],
    {
      courseMemberships: {value: courseMemberships, comparator: isEqual},
      indexedByIdStudents: {value: indexedByIdStudents, comparator: isEqual},
      unarchivedCourses: {value: unarchivedCourses, comparator: isEqual},
    },
  )

  useSafeEffectExtraDeps<{
    onChangeSchool: (propsSchoolId: number) => void
    onAllSchools: () => void
  }>(
    ({onChangeSchool, onAllSchools}) => {
      if (oldSchoolId.current !== propsSchoolId) {
        if (propsSchoolId !== null && propsSchoolId !== undefined) {
          onChangeSchool(propsSchoolId)
          oldSchoolId.current = propsSchoolId
        } else {
          onAllSchools()
        }
      }
    },
    [propsSchoolId],
    {
      onChangeSchool: {value: onChangeSchool, comparator: () => true},
      onAllSchools: {value: onAllSchools, comparator: () => true},
    },
  )

  function updatePathFromState() {
    const query = catMaybes([
      mmap(id => `subject=${id}`, subject),
      mmap(id => `teacherId=${id}`, teacherId),
      mmap(id => `studentId=${id}`, studentId),
      mmap(id => `start=${id.format('x')}`, startDate),
      mmap(id => `end=${id.format('x')}`, endDate),
      mmap(grade => `grade=${grade}`, grade),
      mmap(domain => `domain=${encodeURIComponent(domain)}`, domain),
    ]).join('&')

    navigate(`${Routes.growth(schoolId)}?${query}`)
  }

  function onChangeSubject(subject: SubjectT) {
    setSubject(subject)
    setStartDate(setStartDateValue(subject, startDate))
    setTeacherId(undefined)
    setStudentId(null)
    setDomain(null)
  }

  function onChangeSchool(schoolId: number) {
    navigate(Routes.growth(schoolId))
    setSchoolId(schoolId)
    setTeacherId(undefined)
    setStudentId(null)
  }

  function onAllSchools() {
    navigate(Routes.growth())
    setSchoolId(null)
    setTeacherId(undefined)
    setStudentId(null)
  }

  function onChangeTeacher(teacherId?: number) {
    setTeacherId(teacherId)
    setStudentId(null)
  }

  function onChangeStudent(studentId?: number | null) {
    setStudentId(studentId)
  }

  function onChangeIdp(idpOrigins: AllProvidersOrSpecificT) {
    setIdpOrigins(idpOrigins)
  }

  function onChangeStartDate(date?: Moment | null) {
    if (date) {
      setStartDate(setStartDateValue(subject, date))
    }
  }

  function onChangeEndDate(date?: Moment | null) {
    if (date) {
      setEndDate(date)
    }
  }

  function onChangeGrade(grade?: number | null) {
    setGrade(grade)
  }

  function onChangeDomain(domain?: string | null) {
    setDomain(domain)
  }

  useSafeEffectExtraDeps<{
    updatePathFromState: () => void
    startDate: Moment
    endDate: Moment
    subject: SubjectT
    idpOrigins: AllProvidersOrSpecificT
  }>(
    ({updatePathFromState}) => {
      updatePathFromState()
    },
    [schoolId, studentId, teacherId, domain, grade],
    {
      startDate: {value: startDate, comparator: isEqual},
      endDate: {value: endDate, comparator: isEqual},
      subject: {value: subject, comparator: isEqual},
      idpOrigins: {value: idpOrigins, comparator: isEqual},
      updatePathFromState: {value: updatePathFromState, comparator: () => true},
    },
  )

  function onDrilldown(filters: {[filter: string]: string}) {
    if (filters.school_id !== undefined) {
      onChangeSchool(parseInt(filters.school_id, 10))
    } else if (filters.teacher_id !== undefined) {
      onChangeTeacher(parseInt(filters.teacher_id, 10))
    } else if (filters.student_id !== undefined) {
      onChangeStudent(parseInt(filters.student_id, 10))
    } else if (filters.Grade !== undefined) {
      onChangeGrade(parseInt(filters.Grade, 10))
    } else if (filters.Domains !== undefined) {
      onChangeDomain(filters.Domains)
    } else if (filters.ccss_domains !== undefined) {
      onChangeDomain(filters.ccss_domains)
    } else if (filters.idp_origin !== undefined) {
      onChangeIdp(getDashboardIdp(filters.idp_origin))
    }
  }

  const filteredTeachers =
    schoolId !== null && schoolId !== undefined
      ? filterTeachersBySchoolId(teachers, schoolId)
      : null

  return (
    <Growth
      subject={subject}
      onChangeSubject={onChangeSubject}
      schoolId={schoolId}
      schools={schools}
      onChangeSchool={onChangeSchool}
      onAllSchools={onAllSchools}
      teachers={filteredTeachers}
      teacherId={teacherId}
      onChangeTeacher={onChangeTeacher}
      students={filteredStudents}
      studentId={studentId}
      onChangeStudent={onChangeStudent}
      idpOrigins={idpOrigins}
      onChangeIdp={onChangeIdp}
      startDate={startDate}
      endDate={endDate}
      onChangeStartDate={onChangeStartDate}
      onChangeEndDate={onChangeEndDate}
      grade={grade}
      onChangeGrade={onChangeGrade}
      domain={domain}
      onChangeDomain={onChangeDomain}
      mathDomains={mathDomains}
      elaDomains={elaDomains}
      onDrilldown={onDrilldown}
      mathStandardSet={mathStandardSet}
      schoolAdmin={schoolAdmin}
    />
  )
}

function getFilteredStudents(
  teacherId: number,
  students: StudentsMapT,
  unarchivedCourses: Array<CourseAttrs>,
  courseMemberships: Array<CourseMembershipAttrs>,
  studentId?: number | null,
): Array<StudentAttrs> {
  const filteredStudents = forTeacher(students, teacherId, unarchivedCourses, courseMemberships)

  // Archived students are reachable via the embed.
  // If the selected student is archived they should show in the picker
  if (
    studentId !== null &&
    studentId !== undefined &&
    !some(filteredStudents, s => s.id === studentId)
  ) {
    const archivedStudent = fromJust(
      students.get(studentId),
      `Could not retrieve student with id ${studentId}`,
    )
    const filteredWithArchived = [archivedStudent, ...filteredStudents]
    return filteredWithArchived
  } else {
    return filteredStudents
  }
}

const getDashboardIdp = (idpString: string): AllProvidersOrSpecificT => ({
  tag: 'specific',
  specific: mkNonEmptySingleton(parseIdpOrThrow(idpString)),
})

function parseIdpOrThrow(idpString: string): DashboardIdentityProviderT {
  switch (idpString) {
    case 'manual':
      return 'manual'
    case 'clever':
      return 'clever'
    case 'class-link':
      return 'class-link'
    case 'csv':
      return 'csv'
    case 'renaissance':
      return 'renaissance'
    default:
      throw new Error(`No DashboardIdentityProviderT for idp: ${idpString}`)
  }
}

function filterTeachersBySchoolId(
  teachers: Array<BaseTeacherAttrs>,
  schoolId: number,
): Array<BaseTeacherAttrs> {
  return teachers.filter(teacher => teacher.schoolId === schoolId)
}

function setStartDateValue(subject: SubjectT, start?: Moment | null): Moment {
  if (subject === 'math') {
    return start ?? defaultStartDate
  }

  if (start !== null && start !== undefined) {
    return start.isBefore(DEFAULT_ELA_MIN_DATE) ? DEFAULT_ELA_MIN_DATE : start
  }

  return DEFAULT_ELA_MIN_DATE
}
